import React, { useState } from 'react'
import { useEffect } from 'react';
import BlogNav from './components/BlogNav'
import { gsap } from 'gsap'
import { Container } from 'react-bootstrap';
import BlogCarousel from './components/BlogCarousel';
import Pagination from 'react-bootstrap/Pagination';
import "./Blog.scss"
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Posts from './components/Posts';
import ImageSkeleton from './components/skeletons/ImageSkeleton';
import CarouselSkeleton from './components/skeletons/CarouselSkeleton';
import firebase from 'firebase/compat/app';
import PostModal from './components/PostModals/PostModal';
import { Link } from "react-router-dom"
import { useAuth } from './context/AuthContext';


function Blog() {

  useEffect(() => {
    return () => {
      gsap.to(".App", { duration: .0, css: { visibility: 'visible' } })
    };
  }, []);

  // State variables for posts with images
  const [postsWithImages, setPostsWithImages] = useState([]);
  const [loadingWithImages, setLoadingWithImages] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentPageWithImages, setCurrentPageWithImages] = useState(1);

  // State variables for posts without images
  const [postsWithoutImages, setPostsWithoutImages] = useState([]);
  const [loadingWithoutImages, setLoadingWithoutImages] = useState(false);
  const [currentPageWithoutImages, setCurrentPageWithoutImages] = useState(1);

  // Function to handle click event and open the modal
  const handleOpenModal = (postData, index) => {
    setSelectedPost(postData);
    setSelectedIndex(index);
    setIsOpen(true);
  };

  useEffect(() => {
    // Fetch all posts from Firestore for posts with images
    const fetchPostsWithImages = async () => {
      setLoadingWithImages(true);
      const postsRef = firebase.firestore().collection('Posts').orderBy('Time', 'desc');
      const snapshot = await postsRef.get();
      const postsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setPostsWithImages(postsData.filter((record) => record.PhotoUrl));
      setLoadingWithImages(false);
    };

    // Fetch all posts from Firestore for posts without images
    const fetchPostsWithoutImages = async () => {
      setLoadingWithoutImages(true);
      const postsRef = firebase.firestore().collection('Posts').orderBy('Time', 'desc');
      const snapshot = await postsRef.get();
      const postsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setPostsWithoutImages(postsData.filter((record) => !record.PhotoUrl));
      setLoadingWithoutImages(false);
    };

    fetchPostsWithImages();
    fetchPostsWithoutImages();
  }, []);

  // Pagination variables for posts with images
  const postsPerPageWithImages = 8;
  const lastIndexWithImages = currentPageWithImages * postsPerPageWithImages;
  const firstIndexWithImages = lastIndexWithImages - postsPerPageWithImages;
  const recordsWithImages = postsWithImages.slice(firstIndexWithImages, lastIndexWithImages);
  const npageWithImages = Math.ceil(postsWithImages.length / postsPerPageWithImages);

  // Pagination variables for posts without images
  const postsPerPageWithoutImages = 8;
  const lastIndexWithoutImages = currentPageWithoutImages * postsPerPageWithoutImages;
  const firstIndexWithoutImages = lastIndexWithoutImages - postsPerPageWithoutImages;
  const recordsWithoutImages = postsWithoutImages.slice(firstIndexWithoutImages, lastIndexWithoutImages);
  const npageWithoutImages = Math.ceil(postsWithoutImages.length / postsPerPageWithoutImages);

  // Function to generate limited page numbers for pagination
  const getLimitedPageNumbers = (currentPage, npage, limit) => {
    const halfLimit = Math.floor(limit / 2);
    let startPage;

    if (npage <= limit) {
      // If there are fewer pages than the limit, show all pages
      startPage = 1;
    } else if (currentPage <= halfLimit) {
      // Show first three page numbers when currentPage is less than or equal to halfLimit
      startPage = 1;
    } else if (currentPage >= npage - halfLimit) {
      // Show last three page numbers when currentPage is close to the last page
      startPage = npage - limit + 1;
    } else {
      // Show three page numbers centered around the currentPage
      startPage = currentPage - halfLimit;
    }

    return [...Array(Math.min(npage, limit)).keys()].map((n) => startPage + n);
  };

  const limit = 3; // Change the limit to set the number of visible pages (always 3)
  const limitedPageNumbersWithImages = getLimitedPageNumbers(currentPageWithImages, npageWithImages, limit);
  const limitedPageNumbersWithoutImages = getLimitedPageNumbers(currentPageWithoutImages, npageWithoutImages, limit);

  const handleCloseModal = () => {
    setSelectedPost(null);
    setIsOpen(false);
  };

  // Function to handle pagination changes for posts with images
  function prevPageWithImages() {
    if (currentPageWithImages !== 1) {
      setCurrentPageWithImages(currentPageWithImages - 1);
    }
  }

  function nextPageWithImages() {
    if (currentPageWithImages !== npageWithImages) {
      setCurrentPageWithImages(currentPageWithImages + 1);
    }
  }

  // Function to handle pagination changes for posts without images
  function prevPageWithoutImages() {
    if (currentPageWithoutImages !== 1) {
      setCurrentPageWithoutImages(currentPageWithoutImages - 1);
    }
  }

  function nextPageWithoutImages() {
    if (currentPageWithoutImages !== npageWithoutImages) {
      setCurrentPageWithoutImages(currentPageWithoutImages + 1);
    }
  }

  // Function to handle post deletion
  const handleDeletePost = (postId) => {
    // Remove the deleted post from the postsWithImages state
    setPostsWithImages((prevPosts) => prevPosts.filter((post) => post.id !== postId));

    // Remove the deleted post from the postsWithoutImages state
    setPostsWithoutImages((prevPosts) => prevPosts.filter((post) => post.id !== postId));

    // Close the modal after successful deletion
    setIsOpen(false);
    setSelectedPost(null);
  };


  const { currentUser } = useAuth(); // Get the currently logged-in user
  const [loggedInUserId, setLoggedInUserId] = useState(null);

  useEffect(() => {
    // Update the loggedInUserId state with the current user's ID when the user logs in or out
    if (currentUser) {
      setLoggedInUserId(currentUser.uid);
    } else {
      setLoggedInUserId(null);
    }
  }, [currentUser]);



  return (
    <div className='App'>
      <BlogNav />
      {isOpen && selectedPost && (
        <PostModal
          open={isOpen}
          onClose={handleCloseModal}
          selectedPost={selectedPost}
          rating={selectedPost.rating}
          posts={postsWithImages}
          selectedIndex={selectedIndex}
          setSelectedPost={setSelectedPost}
          loggedInUserId={loggedInUserId}
          onDeletePost={handleDeletePost}
          handleOpenModal={handleOpenModal}
        >
        </PostModal>
      )}

      {loadingWithImages ? (
        <Row xs={1} md={4}>
          <Col className='col-sm'>
            <CarouselSkeleton />
          </Col>
        </Row>
      ) : (
        <BlogCarousel />
      )}

      <section className="latestPostSection">
        <Container fluid className='fluidCont'>
          <div className="headr">
            <p>Latest Blog Posts</p>
          </div>

          <div className="allElse">
            {loadingWithImages ? (
              <Row xs={1} md={4}>
                {[...Array(postsPerPageWithImages)].map((_, index) => (
                  <Col key={`skeleton_${index}`} className='col-sm'>
                    <ImageSkeleton />
                  </Col>
                ))}
              </Row>
            ) : (
              <Row xs={1} md={4}>
                {recordsWithImages.map((record, index) => (
                  <Col key={record.id} className='col-sm posty'>
                    <Link to={`/blog/${record.id}`} onClick={(e) => e.stopPropagation()}>
                      <Posts
                        id={record.id}
                        name={record.name}
                        Content={record.Content}
                        Title={record.Title}
                        PhotoUrl={record.PhotoUrl}
                        Time={record.Time}
                        index={index}
                        setIsOpen={handleOpenModal}
                        onClick={() => handleOpenModal(record, index)}
                        onDeletePost={handleDeletePost}
                      />
                    </Link>
                  </Col>
                ))}
              </Row>
            )}

            <Pagination size='sm' className='pagi'>
              <Pagination.First linkClassName="pagiLink" onClick={() => setCurrentPageWithImages(1)} />
              <Pagination.Prev linkClassName="pagiLink" onClick={prevPageWithImages} />
              {limitedPageNumbersWithImages.map((n, i) => (
                <Pagination.Item
                  className={`pagiIn ${currentPageWithImages === n ? 'active' : ''}`}
                  linkClassName="pagiLinko"
                  onClick={() => setCurrentPageWithImages(n)}
                  key={i}
                >
                  {n}
                </Pagination.Item>
              ))}
              {limitedPageNumbersWithImages[limitedPageNumbersWithImages.length - 1] < npageWithImages && (
                <Pagination.Ellipsis linkClassName="pagiLink dotdot" disabled />
              )}
              {limitedPageNumbersWithImages[limitedPageNumbersWithImages.length - 1] < npageWithImages && (
                <Pagination.Item
                  className={`pagiIn ${currentPageWithImages === npageWithImages ? 'active' : ''}`}
                  linkClassName="pagiLinko"
                  onClick={() => setCurrentPageWithImages(npageWithImages)}
                >
                  {npageWithImages}
                </Pagination.Item>
              )}
              <Pagination.Next linkClassName="pagiLink" onClick={nextPageWithImages} />
              <Pagination.Last linkClassName="pagiLink" onClick={() => setCurrentPageWithImages(npageWithImages)} />
            </Pagination>
          </div>
        </Container>


        <Container fluid className='fluidCont'>
          <div className="headr">
            <p>Articles</p>
          </div>


          <div className="allElse">
            {loadingWithoutImages ? (
              <Row xs={1} md={4}>
                {[...Array(postsPerPageWithoutImages)].map((_, index) => (
                  <Col key={`skeleton_${index}`} className='col-sm'>
                    <ImageSkeleton />
                  </Col>
                ))}
              </Row>
            ) : (
              <Row xs={1} md={4}>
                {recordsWithoutImages.map((record, index) => (
                  <Col key={record.id} className='col-sm posty'>
                    <Link to={`/blog/${record.id}`} onClick={(e) => e.stopPropagation()}>
                      <Posts
                        id={record.id}
                        name={record.name}
                        Content={record.Content}
                        Title={record.Title}
                        PhotoUrl={record.PhotoUrl}
                        Time={record.Time}
                        index={index}
                        setIsOpen={handleOpenModal}
                        onClick={() => handleOpenModal(record, index)}
                        onDeletePost={handleDeletePost}
                      />
                    </Link>
                  </Col>
                ))}
              </Row>
            )}

            <Pagination size='sm' className='pagi'>
              <Pagination.First linkClassName="pagiLink" onClick={() => setCurrentPageWithoutImages(1)} />
              <Pagination.Prev linkClassName="pagiLink" onClick={prevPageWithoutImages} />
              {limitedPageNumbersWithoutImages.map((n, i) => (
                <Pagination.Item
                  className={`pagiIn ${currentPageWithoutImages === n ? 'active' : ''}`}
                  linkClassName="pagiLinko"
                  onClick={() => setCurrentPageWithoutImages(n)}
                  key={i}
                >
                  {n}
                </Pagination.Item>
              ))}
              {limitedPageNumbersWithoutImages[limitedPageNumbersWithoutImages.length - 1] < npageWithoutImages && (
                <Pagination.Ellipsis linkClassName="pagiLink dotdot" disabled />
              )}
              {limitedPageNumbersWithoutImages[limitedPageNumbersWithoutImages.length - 1] < npageWithoutImages && (
                <Pagination.Item
                  className={`pagiIn ${currentPageWithoutImages === npageWithoutImages ? 'active' : ''}`}
                  linkClassName="pagiLinko"
                  onClick={() => setCurrentPageWithoutImages(npageWithoutImages)}
                >
                  {npageWithoutImages}
                </Pagination.Item>
              )}
              <Pagination.Next linkClassName="pagiLink" onClick={nextPageWithoutImages} />
              <Pagination.Last linkClassName="pagiLink" onClick={() => setCurrentPageWithoutImages(npageWithoutImages)} />
            </Pagination>
          </div>
        </Container>

      </section>
    </div>
  )
}



export default Blog
